import { AcumaticaService } from 'src/app/acumatica/acumatica.service';
import { LogService } from 'src/app/services/logging.service';
import { Router } from '@angular/router';
import { PurchaseReceipt, PurchaseReceiptDetail, PurchaseReceiptService } from 'src/app/acumatica/EntityServices/PurchaseReceipt.service';
import { MayerFabricsService } from './../../services/mayer-fabrics.service';
import { ConfirmationDialog } from './../../common/confirmation-dialog/confirmation-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { InventoryIDSelectorComponent, SelectorEvent } from './../../common/inventory-id-selector/inventory-id-selector.component';
import { AlertDialog } from 'src/app/common/alert-dialog/alert-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ItemType, StockItem } from 'src/app/acumatica/EntityServices/StockItem.service';
import { ApplicationScreen } from 'src/app/application-navigator/application-screen';
import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { AudioService, Sound } from 'src/app/services/audio.service';
import { AppConfig } from 'src/app/app-config.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-rep-inventory-initialization',
  templateUrl: './rep-inventory-initialization.component.html',
  styleUrls: ['./rep-inventory-initialization.component.css']
})
export class RepInventoryInitializationComponent extends ApplicationScreen implements AfterViewInit {
  pageTitle = 'Adjust in Inventory';
  inventoryID: string = '';
  inventory: inventoryItem[] = [];
  lastItem?: inventoryItem;
  private numsRegex = /^[0-9]+$/;

  @ViewChild('itemSelector', { static: true }) itemSelector!: InventoryIDSelectorComponent;

  private acumaticaConfig = AppConfig.settings.acumatica;

  constructor(
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private router: Router,
    private audio: AudioService,
    private mayerfabrics: MayerFabricsService,
    private receipts: PurchaseReceiptService,
    private logger: LogService,
    private acumatica: AcumaticaService,
  ) {
    super();

    let whseID = this.mayerfabrics.RepWarehouseID;
    if (!whseID && this.isRepMode) { this.router.navigate(['/']); return }

    this.inventoryID = '';
  }
  ngAfterViewInit(): void {
    this.resetInput(100);
  }

  resetInput(ms?: number, blankField: boolean = false) {
    setTimeout(() => {
      if (blankField) this.itemSelector.reset();
      this.itemSelector.focus();
    }, ms);
  }

  checkForQty(event: SelectorEvent) {
    if (event.value.trim().toUpperCase() == 'WC920') this.itemSelector.inventoryID = 'WC920CD';
    if (event.value.trim().toUpperCase() == '336D') this.itemSelector.inventoryID = '336CD';

    if (this.numsRegex.test(event.value) && this.lastItem !== undefined) {
      let newQty = parseInt(event.value);
      let prevQty = this.lastItem.qty - 1;

      if (this.lastItem.qty > 1) {
        this.dialog.open(ConfirmationDialog, {
          data: {
            message: "You previously had entered a quantity of " + prevQty + " for this item.\n" +
              "Are you correcting that quantity or adding to it?",
            sound: Sound.GentleBeep,
            buttons: [
              { color: 'accent', text: 'Cancel', value: undefined },
              { color: 'accent', text: 'Correct to ' + newQty, value: newQty },
              { color: 'accent', text: 'Additional, ' + (prevQty + newQty) + ' total', value: (prevQty + newQty) }
            ]
          }
        }).afterClosed().subscribe(qty => {
          if (qty === undefined) {
            this.lastItem!.qty = prevQty;
            this.lastItem = undefined;
            this.resetInput(undefined, true);
            return;
          }

          this.isDirty = true;
          this.lastItem!.qty = qty;
          this.checkInventory();
          this.resetInput(undefined, true);
        })
      } else {
        this.isDirty = true;
        this.lastItem.qty = newQty;
        this.checkInventory();
        this.resetInput();
      }

      event.preventDefault = true;
    }
  }

  addItem(item?: StockItem) {
    const nope = (message: string) => {
      this.snackBar.open(message, undefined, { duration: 5000 });
      this.audio.play(Sound.GentleBeep);
      this.resetInput();
    }
    // invalid inventory ID
    if (item === undefined) {
      nope("Invalid Inventory ID");
      return;
    }

    if (item.isRolledGood) {
      nope("Unable to Adjust in Rolled Goods through this process");
      return;
    }

    if (environment.mode == 'rep') switch (item.ItemType) {
      case ItemType.CollatedSet:
      case ItemType.MemoSample:
      case ItemType.Supplies:
      case ItemType.TagSet:
      case ItemType.Unspecified:
        nope("That item type will not be tracked in your inventory");
        return;
    }

    if (item.ItemStatus == 'Inactive' || item.ItemStatus == 'Marked for Deletion') {
      nope("That item has a status of '" + item.ItemStatus + "' and will not be tracked in your inventory");
      return;
    }

    this.isDirty = true;

    // search for the item already in the inventory and increment quantity
    this.lastItem = this.inventory.find(inv => inv.item.InventoryID == item.InventoryID);
    if (this.lastItem) {
      ++this.lastItem.qty;
      this.resetInput();
      return;
    }

    // if not found, add it to the inventory
    this.lastItem = { item: item, qty: 1 };
    this.inventory.unshift(this.lastItem);

    this.resetInput();
  }

  checkInventory() {
    this.inventory.forEach((item, idx) => {
      if (item.qty == 0) this.inventory.splice(idx, 1);
    })
  }

  get isRepMode(): boolean { return environment.mode == 'rep'; }
  get isWMSMode(): boolean { return environment.mode == 'wms'; }

  createReceipt() {
    this.dialog.open(ConfirmationDialog, {
      data: {
        message: this.isRepMode
          ? "Are you sure you're ready to update these items into your personal inventory?"
          : "Adjust these items into Warehouse 03 inventory?",
        confirmText: "Yes, Update It",
        cancelText: "Cancel"
      }
    }).afterClosed().subscribe(response => {
      if (!response) return;

      // record data to logging server
      let logData: any[] = this.inventory.map(inv => { return { item: inv.item.InventoryID, qty: inv.qty } });
      this.logger.log(
        this.acumatica.username,
        ['Adjust in Inventory'],
        JSON.stringify(logData)
      );

      let whseID = this.isWMSMode ? '03' : this.mayerfabrics.RepWarehouseID!;

      let receipt = new PurchaseReceipt({
        VendorID: this.acumaticaConfig.SalesRepInventoryInitalizeVendor,
        Details: []
      });

      this.inventory.forEach(inv => {
        let detail = new PurchaseReceiptDetail();
        detail.InventoryID = inv.item.InventoryID;
        detail.ReceiptQty = inv.qty;
        detail.WarehouseID = whseID;
        detail.Allocations[0].received = true;

        detail.Location = this.isWMSMode
          ? inv.item.DefaultIssueFrom
          : detail.Location = 'MAIN';

        receipt.Details!.push(detail);
      });


      this.receipts.put(receipt, { loader: "Updating Inventory" }).subscribe(receipt => {
        this.receipts.Release2(receipt, { message: "Updating Inventory" }).subscribe(() => {
          this.dialog.open(AlertDialog, {
            data: {
              message: "Inventory has been updated",
              sound: Sound.GentleBeep,
              buttonText: 'OK',
            }
          }).afterClosed().subscribe(() => {
            this.inventory = [];
            this.isDirty = false;
            this.router.navigate(['/']);
          })
        });
      });
    });
  }
}


interface inventoryItem {
  item: StockItem,
  qty: number
}